// Workbook construction and edit operations. Pure functions over the data model
// in types.ts — mutation is in-place and callers bump a version counter, because
// the grid renders off a store (see ui/store.ts), not off structural sharing.

import type { Cell, CellMap, CellValue, FormatId, Sheet, Workbook } from './types';
import { parseCellA1, rowColToA1 } from './address';
import { parseDate } from './dates';
import { formatFamily } from '@/engine/format';

export const DEFAULT_COL_WIDTH = 96;
export const DEFAULT_ROW_HEIGHT = 24;
export const DEFAULT_ROWS = 200;
export const DEFAULT_COLS = 26;

let idSeq = 0;
export function newId(prefix: string): string {
  idSeq += 1;
  return `${prefix}${Date.now().toString(36)}${idSeq.toString(36)}`;
}

/** Sparse-map key. Row/col are 1-based. */
export function ck(row: number, col: number): string {
  return `${row},${col}`;
}

export function parseCk(key: string): { row: number; col: number } {
  const i = key.indexOf(',');
  return { row: Number(key.slice(0, i)), col: Number(key.slice(i + 1)) };
}

export function createSheet(name: string, id = newId('s')): Sheet {
  return {
    id,
    name,
    cells: {},
    colWidths: {},
    rowHeights: {},
    rows: DEFAULT_ROWS,
    cols: DEFAULT_COLS,
    /*
     * Row 1 and column A are frozen from the start.
     *
     * Every sheet this app makes is a table: labels down the left, headers
     * across the top. Scrolling either out of view is the one thing that makes
     * a wide table unreadable, and nobody discovers a right-click menu to fix a
     * problem they have not hit yet. Right-click ▸ Unfreeze if a sheet is not
     * a table.
     */
    freezeRows: 1,
    freezeCols: 1,
  };
}

export const USAGES_SHEET_NAME = 'Usages';

export function createWorkbook(): Workbook {
  const first = createSheet('Sheet1');
  return { sheets: [first], activeSheetId: first.id, blocks: {}, charts: {} };
}

/** The system sheet with this role, if the workbook has one. */
export function sheetByRole(wb: Workbook, role: NonNullable<Sheet['role']>): Sheet | undefined {
  return wb.sheets.find((s) => s.role === role);
}

export function usagesSheet(wb: Workbook): Sheet | undefined {
  return wb.sheets.find((s) => s.role === 'usages');
}

export function isSystemSheet(sheet: Sheet): boolean {
  return sheet.role !== undefined;
}

/**
 * System tabs sit in this order whenever they exist — the order
 * `ensureSystemSheets` creates them in, and the order `normalizeSystemSheetOrder`
 * pins them back into if a drag (or an older, unpinned workbook) disturbs it.
 */
export const SYSTEM_ROLE_ORDER: NonNullable<Sheet['role']>[] = [
  'areas',
  'features',
  'blocks',
  'usages',
  'analytics',
  'measures',
];

/**
 * Collapses every system sheet into one contiguous block at the END of the tab
 * strip, in `SYSTEM_ROLE_ORDER`, leaving the ordinary sheets' own relative order
 * untouched.
 *
 * Needed for two reasons: a workbook saved before pinning existed may have a
 * system sheet anywhere, and `moveSheetToSlot`'s pinning clamp only works if the
 * block it is defending is actually contiguous beforehand.
 */
export function normalizeSystemSheetOrder(wb: Workbook): void {
  const system = SYSTEM_ROLE_ORDER.map((role) => wb.sheets.find((s) => s.role === role)).filter(
    (s): s is Sheet => s !== undefined
  );
  if (system.length === 0) return;
  const users = wb.sheets.filter((s) => s.role === undefined);
  const next = [...users, ...system];
  if (next.length !== wb.sheets.length) return;
  if (next.some((s, i) => s !== wb.sheets[i])) wb.sheets = next;
}

/**
 * Where the pinned system block starts, once normalized — the slot a new
 * ordinary sheet is inserted at, and the boundary `moveSheetToSlot` clamps a
 * drag to.
 */
export function systemBlockStart(wb: Workbook): number {
  const i = wb.sheets.findIndex((s) => s.role !== undefined);
  return i < 0 ? wb.sheets.length : i;
}

export function sheetById(wb: Workbook, id: string): Sheet | undefined {
  return wb.sheets.find((s) => s.id === id);
}

export function sheetByName(wb: Workbook, name: string): Sheet | undefined {
  const lower = name.toLowerCase();
  return wb.sheets.find((s) => s.name.toLowerCase() === lower);
}

export function activeSheet(wb: Workbook): Sheet {
  return sheetById(wb, wb.activeSheetId) ?? wb.sheets[0]!;
}

/** Sheet names must be unique and non-empty; returns a free variant of `want`. */
export function uniqueSheetName(wb: Workbook, want: string, exceptId?: string): string {
  const base = want.trim() || 'Sheet';
  const taken = new Set(
    wb.sheets.filter((s) => s.id !== exceptId).map((s) => s.name.toLowerCase())
  );
  if (!taken.has(base.toLowerCase())) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}${i}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
}

export function addSheet(wb: Workbook, name?: string): Sheet {
  const sheet = createSheet(uniqueSheetName(wb, name ?? `Sheet${wb.sheets.length + 1}`));
  // Before the pinned system block, never after it — see systemBlockStart.
  wb.sheets.splice(systemBlockStart(wb), 0, sheet);
  return sheet;
}

export function getCell(sheet: Sheet, row: number, col: number): Cell | undefined {
  return sheet.cells[ck(row, col)];
}

/**
 * The single write path for a cell. `raw` is exactly what the user typed;
 * a leading `=` makes it a formula, anything else is coerced to a literal.
 * Blank clears the cell but preserves format and style, because clearing a
 * value and clearing formatting are different user intentions.
 */
export function setCellRaw(sheet: Sheet, row: number, col: number, raw: string): void {
  const key = ck(row, col);
  const prev = sheet.cells[key];
  const trimmed = raw.trim();

  if (trimmed === '') {
    if (prev && (prev.fmt || prev.s)) {
      sheet.cells[key] = { fmt: prev.fmt, s: prev.s };
    } else {
      delete sheet.cells[key];
    }
    return;
  }

  const next: Cell = { fmt: prev?.fmt, s: prev?.s };
  if (trimmed.startsWith('=')) {
    next.f = trimmed.slice(1);
  } else {
    const literal = inferLiteral(trimmed);
    next.v = literal.value;
    /*
     * A typed `%`, `$` or date says what the cell is, and outranks the format
     * already on it.
     *
     * It used to be the other way round — an existing format always won — and
     * that turned `85%` typed into a currency cell into `$1`: the value was
     * 0.85 and correct, wearing a face that made it look like a bug. The marker
     * is an instruction, the same as picking the format from the menu.
     *
     * Except within the same FAMILY, where the cell's own format is the more
     * specific of the two: typing `85%` over `12.5%` keeps one decimal place
     * rather than silently coarsening the column.
     */
    if (literal.fmt !== undefined && formatFamily(prev?.fmt) !== formatFamily(literal.fmt)) {
      next.fmt = literal.fmt;
    }
  }
  stripUndefined(next);
  sheet.cells[key] = next;
  growToFit(sheet, row, col);
}

/**
 * Typed text to a value, plus the format that text implies.
 *
 * A date becomes an Excel SERIAL with a date format — not a string — so every
 * built-in date function works on it and so a column of dates can be dragged
 * as a series.
 *
 * `15%` and `$8,685` carry their format too, and that matters most for PASTE:
 * Excel and Google Sheets put the *displayed* text on the clipboard, so without
 * this a pasted column of percentages lands as 0.15, 0.125 — the right numbers
 * wearing the wrong face, which reads as a bug.
 */
export function inferLiteral(text: string): { value: CellValue; fmt?: FormatId } {
  const t = text.trim();
  if (t === '') return { value: '' };

  const looksNumeric = /^[-+]?[\d.,]+$/.test(t);
  if (!looksNumeric) {
    const serial = parseDate(t);
    if (serial !== null) return { value: serial, fmt: 'date' };
  }

  const plain = t.replace(/,/g, '');

  const pct = /^([-+]?(?:\d+\.?\d*|\.\d+))\s*%$/.exec(plain);
  if (pct) {
    const decimals = decimalsOf(pct[1]!);
    return { value: Number(pct[1]) / 100, fmt: decimals >= 2 ? 'percent2' : decimals === 1 ? 'percent1' : 'percent0' };
  }

  const cur = /^([-+]?)[$£€]\s*((?:\d+\.?\d*|\.\d+))$/.exec(plain);
  if (cur) {
    const decimals = decimalsOf(cur[2]!);
    return { value: Number(`${cur[1]}${cur[2]}`), fmt: decimals > 0 ? 'currency2' : 'currency0' };
  }

  return { value: coerceLiteral(t) };
}

function decimalsOf(numeric: string): number {
  const dot = numeric.indexOf('.');
  return dot < 0 ? 0 : numeric.length - dot - 1;
}

/** Write an already-typed value (used by live blocks, paste, and fixtures). */
export function setCellValue(sheet: Sheet, row: number, col: number, v: CellValue, fmt?: FormatId): void {
  const key = ck(row, col);
  const prev = sheet.cells[key];
  if (v === null || v === '') {
    if (prev?.fmt || prev?.s) sheet.cells[key] = { fmt: fmt ?? prev?.fmt, s: prev?.s };
    else delete sheet.cells[key];
    return;
  }
  const next: Cell = { v, fmt: fmt ?? prev?.fmt, s: prev?.s };
  stripUndefined(next);
  sheet.cells[key] = next;
  growToFit(sheet, row, col);
}

export function setCellFormat(sheet: Sheet, row: number, col: number, fmt: FormatId | undefined): void {
  const key = ck(row, col);
  const prev = sheet.cells[key] ?? {};
  const next: Cell = { ...prev, fmt };
  stripUndefined(next);
  if (Object.keys(next).length === 0) delete sheet.cells[key];
  else sheet.cells[key] = next;
  growToFit(sheet, row, col);
}

export function clearCells(sheet: Sheet, r1: number, c1: number, r2: number, c2: number): void {
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      const key = ck(r, c);
      const prev = sheet.cells[key];
      if (!prev) continue;
      if (prev.fmt || prev.s) sheet.cells[key] = { fmt: prev.fmt, s: prev.s };
      else delete sheet.cells[key];
    }
  }
}

/** The text shown in the formula bar — the round-trip of `setCellRaw`. */
export function cellRawText(cell: Cell | undefined): string {
  if (!cell) return '';
  if (cell.f !== undefined) return `=${cell.f}`;
  if (cell.v === undefined || cell.v === null) return '';
  return String(cell.v);
}

export function colWidth(sheet: Sheet, col: number): number {
  return sheet.colWidths[col] ?? DEFAULT_COL_WIDTH;
}

export function rowHeight(sheet: Sheet, row: number): number {
  return sheet.rowHeights[row] ?? DEFAULT_ROW_HEIGHT;
}

/** Bounding box of everything written on the sheet, or null when empty. */
export function usedBounds(sheet: Sheet): { r1: number; c1: number; r2: number; c2: number } | null {
  let r1 = Infinity;
  let c1 = Infinity;
  let r2 = 0;
  let c2 = 0;
  for (const key of Object.keys(sheet.cells)) {
    const { row, col } = parseCk(key);
    if (row < r1) r1 = row;
    if (col < c1) c1 = col;
    if (row > r2) r2 = row;
    if (col > c2) c2 = col;
  }
  if (r2 === 0) return null;
  return { r1, c1, r2, c2 };
}

export function a1(sheet: Sheet, row: number, col: number): string {
  void sheet;
  return rowColToA1(row, col);
}

/** Accepts `A1` or `1,1`; used by tests and the go-to box. */
export function resolveAddress(text: string): { row: number; col: number } | null {
  const cell = parseCellA1(text);
  if (cell) return { row: cell.row, col: cell.col };
  const m = /^(\d+)\s*,\s*(\d+)$/.exec(text.trim());
  if (m) return { row: Number(m[1]), col: Number(m[2]) };
  return null;
}

// ---------------------------------------------------------------------------

function growToFit(sheet: Sheet, row: number, col: number): void {
  if (row > sheet.rows) sheet.rows = row + 20;
  if (col > sheet.cols) sheet.cols = col + 5;
}

function stripUndefined(cell: Cell): void {
  for (const k of Object.keys(cell) as (keyof Cell)[]) {
    if (cell[k] === undefined) delete cell[k];
  }
}

/**
 * Typed-text coercion. Percentages and currency-prefixed numbers are recognised
 * because typing `12%` and getting the string "12%" is the single most common
 * complaint about naive grids. Anything ambiguous stays text — a string that
 * turns silently into a number is worse than one that doesn't.
 */
export function coerceLiteral(text: string): CellValue {
  const t = text.trim();
  if (t === '') return '';
  if (/^(true|false)$/i.test(t)) return t.toLowerCase() === 'true';
  const plain = t.replace(/,/g, '');
  if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(plain)) return Number(plain);
  const pct = /^([-+]?(?:\d+\.?\d*|\.\d+))\s*%$/.exec(plain);
  if (pct) return Number(pct[1]) / 100;
  const cur = /^([-+]?)[$£€]\s*((?:\d+\.?\d*|\.\d+))$/.exec(plain);
  if (cur) return Number(`${cur[1]}${cur[2]}`);
  return t;
}

export function cloneCellMap(cells: CellMap): CellMap {
  const out: CellMap = {};
  for (const [k, v] of Object.entries(cells)) out[k] = { ...v, s: v.s ? { ...v.s } : undefined };
  for (const v of Object.values(out)) if (v.s === undefined) delete v.s;
  return out;
}
